/**
 * Thin, safe wrapper around PostHog (loaded globally in index.html).
 *
 * Why a wrapper:
 * - Never crashes if PostHog is blocked (ad-blockers / network failures).
 * - Centralizes event names so we don't have typos sprinkled across components.
 * - Lets us add or swap providers later without touching every call site.
 */

const isProd = typeof window !== 'undefined' && window.location && window.location.hostname;

const safeCall = (fn) => {
  if (typeof window === 'undefined') return;
  if (!window.posthog) return;
  try {
    fn(window.posthog);
  } catch (_) {
    /* analytics must never break the app */
  }
};

/**
 * Track a named event with optional properties.
 * Examples:
 *   track('analysis_started', { ticker: 'HAL', event: 'UAE OPEC exit' })
 *   track('payment_completed', { amount_usd: 1, analysis_id: 'abc' })
 */
export const track = (eventName, props = {}) => {
  if (!eventName) return;
  safeCall((ph) => ph.capture(eventName, props || {}));
};

/**
 * Identify a user (only useful once they have a stable id, e.g. Stripe email).
 */
export const identify = (distinctId, props = {}) => {
  if (!distinctId) return;
  safeCall((ph) => ph.identify(distinctId, props || {}));
};

/**
 * Register properties that should attach to every subsequent event in this session.
 */
export const registerSessionProps = (props = {}) => {
  safeCall((ph) => ph.register(props || {}));
};

// Centralized event-name constants — always import these instead of hard-coding strings
export const EVENTS = Object.freeze({
  HOMEPAGE_VIEWED: 'homepage_viewed',
  SAMPLE_REPORT_VIEWED: 'sample_report_viewed',
  SAMPLE_LINK_CLICKED: 'sample_link_clicked',
  ANALYSIS_STARTED: 'analysis_started',
  ANALYSIS_READY: 'analysis_ready',
  ANALYSIS_FAILED: 'analysis_failed',
  PAYWALL_SHOWN: 'paywall_shown',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_CANCELLED: 'checkout_cancelled',
  PAYMENT_COMPLETED: 'payment_completed',
  OWNER_MODE_ACTIVATED: 'owner_mode_activated',
  OWNER_BYPASS_USED: 'owner_bypass_used',
  REPORT_DOWNLOADED: 'report_downloaded',
});
